import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCol,
  CDataTable,
  CFormGroup,
  CListGroup,
  CListGroupItem,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";

import { Spinner } from "react-bootstrap";
import { getItem, getList } from "../../api/generics";

import { SUCCESS } from "../../utils/constants/tags";

import { RootState } from "../../store";
import { useSelector } from "react-redux";

import StoreSale, {
  StoreSaleItem,
  calculateItemIVA,
  getStoreSaleItemTaxType,
} from "../../models/store-sale";
import StoreExchange, {
  StoreExchangeItem,
  getDeliveredItems,
  getReturnedItems,
  getExchangeDifferenceColor,
  getExchangeDifferenceLabel,
} from "../../models/store-exchange";

import { formatDateTime } from "../../utils/dates";
import { defaultValueOnUndefined } from "../../utils/fields";
import { getPaymentTypeLabel } from "../../models/client";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import CashbackInfoBadge from "../utils/CashbackInfoBadge";
const lungorLogo = require("../../assets/img/lungorlogo.jpg");

interface SaleDetailProps {
  storeSale?: StoreSale;
  onCancel: () => void | Promise<void>;
}

const SaleDetail: React.FC<SaleDetailProps> = ({ storeSale, onCancel }) => {
  const fields = [
    {
      key: "amount",
      _classes: ["text-center"],
      label: "Cant. Total",
    },
    {
      key: "returnedAmount",
      _classes: ["text-center"],
      label: "Cant. Dev./Cambio",
    },
    {
      key: "name",
      _classes: ["text-center"],
      label: "Concepto",
    },
    {
      key: "taxType",
      _classes: ["text-center"],
      label: "Tipo IVA",
    },
    {
      key: "unitPrice",
      _classes: ["text-center"],
      label: "Precio unitario",
    },
    {
      key: "ivaTotal",
      _classes: ["text-center"],
      label: "IVA Total",
    },
    {
      key: "totalPrice",
      _classes: ["text-center"],
      label: "Subtotal",
    },
  ];

  const company = useSelector((state: RootState) => state.company.data.company);
  const [saleItems, setSaleItems] = useState<StoreSaleItem[]>([]);
  const [exchanges, setExchanges] = useState<StoreExchange[]>([]);

  const [loading, setLoading] = useState(false);

  const getSaleItems = async () => {
    if (storeSale === undefined || storeSale.id === undefined) {
      setSaleItems([]);
      return;
    }
    const saleIdStr = storeSale.id.toString();
    if (saleIdStr === undefined) {
      setSaleItems([]);
      return;
    }
    const limit = 2000;
    const offset = 0;
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", company.id!.toString());
    additionalParams.set("store_sale_id", saleIdStr);
    setLoading(true);
    const saleItemsStatus = await getList<StoreSaleItem>(
      `/store_sales/items/`,
      limit,
      offset,
      additionalParams
    );
    if (saleItemsStatus.status === SUCCESS) {
      if (saleItemsStatus.data !== undefined) {
        setSaleItems(saleItemsStatus.data.items);
      } else {
        setSaleItems([]);
        setLoading(false);
        return;
      }
    } else {
      setSaleItems([]);
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const getExchanges = async () => {
    if (storeSale === undefined || storeSale.id === undefined) {
      setExchanges([]);
      return;
    }
    const additionalParams: Map<string, string> = new Map<string, string>();
    additionalParams.set("original_sale_id", storeSale.id.toString());
    const exchangesStatus = await getList<StoreExchange>(
      `/store_exchanges/`,
      2000,
      0,
      additionalParams
    );
    if (
      exchangesStatus.status !== SUCCESS ||
      exchangesStatus.data === undefined
    ) {
      setExchanges([]);
      return;
    }
    // The list endpoint only returns headers; fetch each detail for its items.
    const detailed = await Promise.all(
      exchangesStatus.data.items.map(async (exchange) => {
        if (exchange.id === undefined) {
          return exchange;
        }
        const detailStatus = await getItem<StoreExchange>(
          `/store_exchanges/${exchange.id}/`
        );
        if (detailStatus.status === SUCCESS && detailStatus.data !== undefined) {
          return detailStatus.data;
        }
        return exchange;
      })
    );
    setExchanges(detailed);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setSaleItems([]);
    setExchanges([]);
  };

  useEffect(() => {
    getSaleItems();
    getExchanges();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSale]);

  if (loading) {
    return (
      <Spinner
        animation="grow"
        style={{
          height: "17px",
          width: "17px",
          marginTop: "auto",
          marginBottom: "auto",
          marginRight: "10px",
        }}
      />
    );
  }

  return (
    <>
      <fieldset disabled={loading}>
        <CRow className={"justify-content-center"}>
          <CCol lg="12">
            <CCard>
              <CRow>
                <CCol lg="12" className={"d-flex justify-content-around"}>
                  <div className="p-3 align-middle">
                    <img
                      src={lungorLogo}
                      alt="logo"
                      style={{ height: "100%", maxHeight: "100px" }}
                    />
                  </div>
                  <div className="p-3 d-flex align-items-center">
                    <h2>
                      Detalle de{" "}
                      {storeSale?.isReturnOperation ? "Devolución" : "Venta"}
                    </h2>
                    {storeSale?.sourceExchangeId ? (
                      <CBadge color={"info"} className={"ml-2"}>
                        {`Diferencia de cambio #${storeSale.sourceExchangeId}`}
                      </CBadge>
                    ) : null}
                    {storeSale?.cancelled ? (
                      <CBadge color={"danger"} className={"ml-2"}>
                        Operación Anulada
                      </CBadge>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="p-3 d-flex align-items-center">
                    <h5>
                      {storeSale?.saleDate
                        ? formatDateTime(new Date(storeSale.saleDate))
                        : ""}
                    </h5>
                  </div>
                </CCol>
              </CRow>
            </CCard>
          </CCol>
        </CRow>
        <CRow>
          <CCol lg="12">
            <CRow>
              <CCol lg="6">
                <CListGroup>
                  <CListGroupItem>
                    {`Sucursal: ${defaultValueOnUndefined(
                      "-",
                      storeSale?.storeName
                    )}`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`Cliente: ${defaultValueOnUndefined(
                      "SIN NOMBRE",
                      storeSale?.clientName
                    )}`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`Razón Social: ${defaultValueOnUndefined(
                      "-",
                      storeSale?.clientLegalName
                    )}`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`RUC: ${defaultValueOnUndefined(
                      "-",
                      storeSale?.clientRuc
                    )}`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`Nro de documento: ${defaultValueOnUndefined(
                      "-",
                      storeSale?.clientDocument
                    )}`}
                  </CListGroupItem>
                </CListGroup>
              </CCol>
              <CCol lg="6">
                <CListGroup>
                  <CListGroupItem>
                    {`Total Venta: ${
                      storeSale?.discountedPrice
                        ? formatToCurrency(storeSale.discountedPrice, PYG)
                        : "-"
                    }`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`IVA: ${
                      storeSale?.iva
                        ? formatToCurrency(storeSale.iva, PYG)
                        : "-"
                    }`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`Nro de factura: ${
                      storeSale?.documentNumber
                        ? `${storeSale.establishment}-${storeSale.dispatchPoint}-${storeSale.documentNumber}`
                        : "-"
                    }`}
                  </CListGroupItem>
                  <CListGroupItem>
                    {`Tipo de Pago: ${
                      storeSale?.paymentType
                        ? getPaymentTypeLabel(storeSale)
                        : "-"
                    }`}
                  </CListGroupItem>
                  {storeSale?.totalDiscount !== undefined &&
                  storeSale?.totalDiscount !== 0 ? (
                    <CListGroupItem>
                      {`Descuento Total realizado: ${formatToCurrency(
                        storeSale.totalDiscount,
                        PYG
                      )}`}
                    </CListGroupItem>
                  ) : (
                    <></>
                  )}
                  {/* Solo se muestra si la venta generó reintegro: es plata que
                      salió del cajón aparte del total facturado. El neto cierra
                      la cuenta para no tener que restarlo a mano. */}
                  {storeSale?.cashbackAmount ? (
                    <>
                      <CListGroupItem>
                        <CashbackInfoBadge />
                        {`Reintegro: - ${formatToCurrency(
                          storeSale.cashbackAmount,
                          PYG
                        )}`}
                      </CListGroupItem>
                      <CListGroupItem>
                        <b>
                          {`Total neto (menos reintegro): ${formatToCurrency(
                            (storeSale.discountedPrice ?? 0) -
                              storeSale.cashbackAmount,
                            PYG
                          )}`}
                        </b>
                      </CListGroupItem>
                    </>
                  ) : (
                    <></>
                  )}
                </CListGroup>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
        <div>
          <CDataTable
            noItemsView={<h2 className="text-center">Sin Resultados</h2>}
            addTableClasses={"table-fixed"}
            fields={fields}
            items={saleItems}
            striped
            border
            loading={loading}
            responsive
            scopedSlots={{
              returnedAmount: (item: StoreSaleItem) => {
                const total =
                  (item.returnedAmount || 0) + (item.exchangedAmount || 0);
                return (
                  <td className="text-center">{total > 0 ? total : "-"}</td>
                );
              },
              taxType: (item: StoreSaleItem) => {
                return (
                  <td className="text-center">
                    {item.taxType ? getStoreSaleItemTaxType(item) : "-"}
                  </td>
                );
              },
              name: (item: StoreSaleItem) => {
                return (
                  <td className="text-center">
                    {defaultValueOnUndefined("No encontrado", item.name)}
                  </td>
                );
              },
              unitPrice: (item: StoreSaleItem) => {
                return (
                  <td className="text-center">
                    {item.unitPrice
                      ? formatToCurrency(item.unitPrice, PYG)
                      : "-"}
                  </td>
                );
              },
              ivaTotal: (item: StoreSaleItem) => {
                return (
                  <td className="text-center">
                    {formatToCurrency(calculateItemIVA(item), PYG)}
                  </td>
                );
              },
              totalPrice: (item: StoreSaleItem) => {
                return (
                  <td className="text-center">
                    {item.totalPrice
                      ? formatToCurrency(item.totalPrice, PYG)
                      : "-"}
                  </td>
                );
              },
            }}
          />
        </div>

        {exchanges.length > 0 ? (
          <CRow className={"mt-4"}>
            <CCol lg="12">
              <h4>Cambios realizados sobre esta venta</h4>
              {exchanges.map((exchange) => {
                const returnedItems = getReturnedItems(exchange);
                const deliveredItems = getDeliveredItems(exchange);
                const describeItem = (item: StoreExchangeItem) =>
                  `${item.amount}x ${defaultValueOnUndefined(
                    "-",
                    item.productName
                  )} ${defaultValueOnUndefined(
                    "",
                    item.colorName
                  )} ${defaultValueOnUndefined("", item.sizeName)}`.trim();
                return (
                  <CCard key={exchange.id} className={"p-3 mt-2"}>
                    <CRow>
                      <CCol lg="6">
                        <strong>{`Cambio #${exchange.id}`}</strong>
                        {exchange.reverted ? (
                          <CBadge color={"danger"} className={"ml-2"}>
                            Revertido
                          </CBadge>
                        ) : null}
                        <div className="text-muted">
                          {exchange.exchangeDate
                            ? formatDateTime(new Date(exchange.exchangeDate))
                            : ""}
                        </div>
                      </CCol>
                      <CCol lg="6" className={"text-right"}>
                        <strong className={"mr-2"}>Diferencia:</strong>
                        <CBadge
                          color={getExchangeDifferenceColor(
                            exchange.priceDifference
                          )}
                        >
                          {getExchangeDifferenceLabel(exchange.priceDifference)}
                          {exchange.priceDifference !== undefined &&
                          exchange.priceDifference !== 0
                            ? ` · ${formatToCurrency(
                                Math.abs(exchange.priceDifference),
                                PYG
                              )}`
                            : ""}
                        </CBadge>
                      </CCol>
                    </CRow>
                    <CRow className={"mt-2"}>
                      <CCol lg="6">
                        <strong>Devolvió:</strong>
                        <ul className={"mb-0"}>
                          {returnedItems.map((item) => (
                            <li key={item.id}>{describeItem(item)}</li>
                          ))}
                        </ul>
                      </CCol>
                      <CCol lg="6">
                        <strong>Se llevó:</strong>
                        <ul className={"mb-0"}>
                          {deliveredItems.map((item) => (
                            <li key={item.id}>{describeItem(item)}</li>
                          ))}
                        </ul>
                      </CCol>
                    </CRow>
                  </CCard>
                );
              })}
            </CCol>
          </CRow>
        ) : null}

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            {/*storeSale?.isReturnOperation?<></>:<CButton type="submit" color="primary" onClick={onSave}>
                {loading ? (
                  <Spinner
                    animation="grow"
                    style={{
                      height: "17px",
                      width: "17px",
                      marginTop: "auto",
                      marginBottom: "auto",
                      marginRight: "10px",
                    }}
                  />
                ) : (
                  <></>
                )}
                {loading ? "Procesando..." : "Devolución"}
              </CButton>*/}
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default SaleDetail;
